import React, { useEffect, useState } from "react";
import { Modal, Form as FormLayout } from "react-bootstrap";
import { useUpdateThemeImagesMutation } from "../../../../../redux/apiSlice";
import ImageInput from "../../../Common/components/image-input";
import TextInput from "../../../Common/components/text-input";
import cogoToast from "cogo-toast";
import * as Yup from "yup";
import { Form, Formik, useFormikContext } from "formik";
import { readFileAsync } from "../../../../util/Helpers";
import useColorThief from 'use-color-thief';
import Compressor from 'compressorjs';

const colorHexRegex = /^0x([0-9a-fA-F]{6})$/;

const to0xColor = (value) => {
    if (!value) return "";
    const v = `${value}`.trim();
    if (/^0x[0-9a-fA-F]{6}$/.test(v)) return `0x${v.slice(2).toUpperCase()}`;
    if (/^#[0-9a-fA-F]{6}$/.test(v)) return `0x${v.slice(1).toUpperCase()}`;
    if (/^[0-9a-fA-F]{6}$/.test(v)) return `0x${v.toUpperCase()}`;
    return v;
}

const toHashColor = (value) => {
    if (!value) return "#000000";
    const v = `${value}`.trim();
    if (/^0x[0-9a-fA-F]{6}$/.test(v)) return `#${v.slice(2).toUpperCase()}`;
    if (/^#[0-9a-fA-F]{6}$/.test(v)) return `#${v.slice(1).toUpperCase()}`;
    if (/^[0-9a-fA-F]{6}$/.test(v)) return `#${v.toUpperCase()}`;
    return "#000000";
}

const ThemeColorPickers = () => {
    const { values, setFieldValue } = useFormikContext();
    return (
        <FormLayout.Row>
            <div className="form-group col">
                <label className={"capitalize text-sm"}>Banner Color Picker</label>
                <input
                    className={"form-control"}
                    type="color"
                    value={toHashColor(values?.bannerBackgroundColor)}
                    onChange={(e) => setFieldValue("bannerBackgroundColor", to0xColor(e.target.value))}
                />
            </div>
            <div className="form-group col">
                <label className={"capitalize text-sm"}>Logo Color Picker</label>
                <input
                    className={"form-control"}
                    type="color"
                    value={toHashColor(values?.logoBackgroundColor)}
                    onChange={(e) => setFieldValue("logoBackgroundColor", to0xColor(e.target.value))}
                />
            </div>
        </FormLayout.Row>
    );
};

const AutoFillThemeColors = ({ bannerColor, logoColor, themeDetails }) => {
    const { values, setFieldValue } = useFormikContext();

    useEffect(() => {
        if (!bannerColor) return;
        if (values?.bannerBackgroundColor) return;
        setFieldValue("bannerBackgroundColor", to0xColor(bannerColor));
    }, [bannerColor, setFieldValue, values?.bannerBackgroundColor]);

    useEffect(() => {
        if (!logoColor) return;
        if (values?.logoBackgroundColor) return;
        setFieldValue("logoBackgroundColor", to0xColor(logoColor));
    }, [logoColor, setFieldValue, values?.logoBackgroundColor]);

    useEffect(() => {
        const metaBanner = themeDetails?.meta?.bannerBackgroundColor;
        const metaLogo = themeDetails?.meta?.logoBackgroundColor;
        if (metaBanner && !values?.bannerBackgroundColor) {
            setFieldValue("bannerBackgroundColor", to0xColor(metaBanner));
        }
        if (metaLogo && !values?.logoBackgroundColor) {
            setFieldValue("logoBackgroundColor", to0xColor(metaLogo));
        }
    }, [setFieldValue, themeDetails?.meta?.bannerBackgroundColor, themeDetails?.meta?.logoBackgroundColor, values?.bannerBackgroundColor, values?.logoBackgroundColor]);

    return null;
};

const EditThemeImagesModal = ({ showEditThemeImagesModal, onHide, themeDetails }) => {
    const [
        uploadThemeImages, // This is the mutation trigger
        {
            isLoading: isUploading,
            isSuccess: uploadedSuccessfully,
        }, // This is the destructured mutation result
    ] = useUpdateThemeImagesMutation()

    /* Close Modal on Successful Update */
    useEffect(() => {
        if (uploadedSuccessfully) {
            cogoToast.success("Created 'Theme Images Update' Action Successfully")
            onHide();
        }
    }, [uploadedSuccessfully]);

    const initialSource = {
        banner: themeDetails?.meta?.banner,
        logo: themeDetails?.meta?.logo,
        webSpotlightImage: themeDetails?.meta?.webSpotlightImage,
        mobileSpotlightImage: themeDetails?.meta?.mobileSpotlightImage,
    }

    const [source, setSource] = useState(initialSource)

    useEffect(() => {
        setSource(initialSource);
    }, [themeDetails])

    const { color: bannerColor } = useColorThief(source.banner, {
        format: 'hex',
        colorCount: 10,
        quality: 10,
    });

    const { color: logoColor } = useColorThief(source.logo, {
        format: 'hex',
        colorCount: 10,
        quality: 10,
    });

    /* Update Theme Images */
    const submitData = async data => {
        data.bannerBackgroundColor = to0xColor(data.bannerBackgroundColor)
        data.logoBackgroundColor = to0xColor(data.logoBackgroundColor)

        const flags = {}
        for (const key in data) {
            if (data[key] instanceof File) {
                data[key] = await readFileAsync(data[key])
                flags[key] = true
            }
        }

        uploadThemeImages(
            {
                id: themeDetails?.id,
                name: themeDetails?.name,
                ...data,
                meta: themeDetails?.meta,
                flags
            }
        )
    }

    const onFileSelected = (e, fieldName, setFieldValue) => {
        const file = e.target.files[0];

        new Compressor(file, {
            maxHeight: 480,
            quality: 1,
            success: (compressedResult) => {
                const resizedFile = new File([compressedResult], file?.name)
                
                setSource({ ...source, [fieldName]: URL.createObjectURL(resizedFile) })
                setFieldValue(fieldName, resizedFile)
                validateFile(resizedFile);
            },
        });
    }

    const validateFile = (file) => {
        const fileName = file.name?.toLowerCase();
        if (!(fileName.endsWith(".jpg") || fileName.endsWith(".png") || fileName.endsWith(".jpeg"))) {
            cogoToast.error("Invalid File Selection");
            return
        }

        if (file.size > 10000000) {
            cogoToast.error("File too large (10mb Max)");
        }
    }

    return (
        <Modal
            show={showEditThemeImagesModal}
            onHide={onHide}
            size="lg"
            aria-labelledby="contained-modal-title-vcenter"
            backdrop={isUploading ? "static" : true}
            keyboard={!isUploading}
        >
            <Formik
                enableReinitialize
                initialValues={
                    {
                        banner: themeDetails?.meta?.banner,
                        logo: themeDetails?.meta?.logo,
                        webSpotlightImage: themeDetails?.meta?.webSpotlightImage,
                        mobileSpotlightImage: themeDetails?.meta?.mobileSpotlightImage,
                        bannerBackgroundColor: to0xColor(themeDetails?.meta?.bannerBackgroundColor) || "",
                        logoBackgroundColor: to0xColor(themeDetails?.meta?.logoBackgroundColor) || "",
                    }
                }
                validationSchema={
                    Yup.object({
                        logo: Yup.string().required('Required'),
                        banner: Yup.string().required('Required'),
                        webSpotlightImage: Yup.string().required('Required'),
                        mobileSpotlightImage: Yup.string().required('Required'),
                        bannerBackgroundColor: Yup.string().matches(colorHexRegex, "Invalid hex color").required('Required'),
                        logoBackgroundColor: Yup.string().matches(colorHexRegex, "Invalid hex color").required('Required'),
                    })
                }
                onSubmit={async (values) => submitData(values)}
            >
                {({ setFieldValue }) => (
                    <Form>
                        <AutoFillThemeColors bannerColor={bannerColor} logoColor={logoColor} themeDetails={themeDetails} />
                        <Modal.Header>
                            <Modal.Title id="contained-modal-title-vcenter">
                                Upload Theme Images
                            </Modal.Title>
                            <button type="button" className="close" onClick={onHide}>
                                <span aria-hidden="true" className="d-block">×</span>
                                <span className="sr-only">Close</span>
                            </button>
                        </Modal.Header>

                        <Modal.Body style={{ maxHeight: "calc(100vh - 300px)", overflow: "auto" }}>
                            <FormLayout.Row>
                                <ImageInput
                                    label="Logo*"
                                    name="logo"
                                    onChange={(e) => onFileSelected(e, "logo", setFieldValue)}
                                />
                                <ImageInput
                                    label="Banner*"
                                    name="banner"
                                    onChange={(e) => onFileSelected(e, "banner", setFieldValue)}
                                />
                            </FormLayout.Row>
                            <FormLayout.Row>
                                <TextInput
                                    label="Banner Background Color*"
                                    name="bannerBackgroundColor"
                                    type="text"
                                    placeholder="0xRRGGBB"
                                />
                                <TextInput
                                    label="Logo Background Color*"
                                    name="logoBackgroundColor"
                                    type="text"
                                    placeholder="0xRRGGBB"
                                />
                            </FormLayout.Row>
                            <ThemeColorPickers />
                            <FormLayout.Row>
                                <ImageInput
                                    label="Spotlight Image (Web)*"
                                    name="webSpotlightImage"
                                    onChange={(e) => onFileSelected(e, "webSpotlightImage", setFieldValue)}
                                />
                                <ImageInput
                                    label="Spotlight Image (Mobile)*"
                                    name="mobileSpotlightImage"
                                    onChange={(e) => onFileSelected(e, "mobileSpotlightImage", setFieldValue)}
                                />
                            </FormLayout.Row>
                        </Modal.Body>
                        <Modal.Footer>
                            <button
                                type="button"
                                onClick={onHide}
                                disabled={isUploading}
                                className="btn btn-light btn-elevate px-4"
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                disabled={isUploading}
                                className="btn btn-primary btn-elevate px-4 ml-4"
                            >
                                Upload
                            </button>
                        </Modal.Footer>
                    </Form>
                )}
            </Formik>
        </Modal>
    );
}

export default EditThemeImagesModal;
